<template>
  <div class="state-input-button">
    <div v-if="title" class="state-input-button_title">
      {{ $t(title) }}<span v-if="required">*</span>
      <span v-if="textLength" class="text-number">{{ textNumber }}/{{ textLength }}</span>
      <div class="top-right">
        <slot name="top-right" />
      </div>
    </div>
    <ValidationProvider
      v-slot="{ errors }"
      :rules="rules"
      :custom-messages="_customMessages"
      class="state-input-button_input-item-wrap"
    >
      <input type="hidden" :value="value" />
      <slot name="input" :errors="errors">
        <div
          class="state-input-button_input-item"
          :class="{ error: errors.length > 0 }"
          :style="{ position: 'relative' }"
        >
          <i v-if="leadingIcon" :class="leadingIcon" class="icon leading-icon" />
          <input
            ref="input"
            :value="value"
            :type="type"
            :disabled="disabled"
            :placeholder="$t(placeholder)"
            :maxlength="textLength"
            :class="{
              'pl-48': leadingIcon,
              'pr-48': trailingIcon,
            }"
            @input="$_valueChange($event)"
            @keyup.enter="$_changeValueEnter($event)"
            @paste="$emit('paste')"
          />
          <i
            v-if="trailingIcon"
            :class="trailingIcon"
            class="icon trailing-icon"
            @click.prevent.stop="$emit('click')"
          />
          <LvTooltip v-if="_isShowTooltip" :value="$t(placeholder)" />
        </div>
      </slot>
      <div v-if="errors.length > 0" class="invalid-text">
        <i class="icon-warning invalid-icon" />
        <p v-for="(text_e, text_k) in errors" :key="text_k" class="text">
          {{ $t(text_e) }}
        </p>
      </div>
      <WsTextButton001
        v-if="btnText"
        font-size-xs
        primary
        no-padding
        is-icon-sm
        :leading-icon="btnIcon"
        @click="$_btnClick()"
      >
        {{ btnText }}
      </WsTextButton001>
    </ValidationProvider>
  </div>
</template>
<script>
import { ValidationProvider } from 'vee-validate';
import { extend } from 'vee-validate';
import {
  required,
  email,
  min,
  max,
  between,
  min_value,
  max_value,
  numeric,
} from 'vee-validate/dist/rules';
extend('required', {
  ...required,
  message: '此項目為必填',
});
extend('email', {
  ...email,
  message: 'email格式不符',
});
extend('between', {
  ...between,
  message: '介於{min}{max}之間',
});
extend('max', {
  ...max,
  // message: "不得多於{length}位"
  message: '不得多於{length}位',
});
extend('min', {
  ...min,
  // message: "不得少於{length}位"
  message: '不得少於{length}位',
});
extend('min_value', {
  ...min_value,
  // message: "不得少於{length}位"
  message: '不得少於{min}',
});
extend('max_value', {
  ...max_value,
  // message: "不得少於{length}位"
  message: '不得大於{max}',
});
extend('customedMinValue', {
  ...min_value,
  // message: "不得少於{length}位"
  message: '不得少於{min}',
});
extend('customedMaxValue', {
  ...max_value,
  // message: "不得少於{length}位"
  message: '不得大於{max}',
});
extend('customedBetween', {
  ...between,
  message: '介於{min}{max}之間',
});
extend('numeric', {
  ...numeric,
  message: '此欄只能輸入數字',
});
export default {
  name: 'StateInputButton',
  components: {
    ValidationProvider,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number],
      default: null,
    },
    textLength: {
      type: String,
      default: '',
    },
    leadingIcon: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    textNumber: {
      type: Number,
      default: 0,
    },
    rules: {
      type: String,
      default: '',
    },
    btnText: {
      type: String,
      default: '',
    },
    btnIcon: {
      type: String,
      default: 'icon-warning',
    },
    type: {
      type: String,
      default: 'text',
    },
    errorMessages: {
      type: String,
      default: '',
    },
    customMessages: {
      type: Object,
    },
    defaultValue: {
      type: [String, Number, Object, Array],
      default: null,
    },
    trailingIcon: {
      type: String,
      default: '',
    },
    isInt: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputContent: '',
      error: false,
      placeholderWidth: '',
      inputWidth: '',
    };
  },
  computed: {
    _customMessages() {
      if (this.customMessages) {
        return this.customMessages;
      } else {
        return {
          customedMinValue: this.errorMessages,
          customedMaxValue: this.errorMessages,
          customedBetween: this.errorMessages,
        };
      }
    },
    _isShowTooltip() {
      if (this.value) return false;
      return this.placeholderWidth > this.inputWidth;
    },
  },
  mounted() {
    this.$_updateWidth();
    window.addEventListener('resize', this.$_updateWidth);
    this.isMounted = true;
    this.inputContent = this.value;
    if (this.value === null && this.defaultValue !== null) {
      this.$emit('input', this.defaultValue);
    } else {
      this.$emit('input', this.value);
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.$_updateWidth);
  },
  methods: {
    $_valueChange(e) {
      if (!this.isMounted) return;
      if (this.type === 'number' && this.isInt)
        this.$emit('input', e.target.value.replace(/[^0-9-]/g, ''));
      else this.$emit('input', e.target.value);
    },
    $_changeValueEnter(e) {
      if (!this.isMounted) return;
      this.$emit('enter', e.target.value);
    },
    $_btnClick() {
      if (!this.isMounted) return;
      this.$emit('btn-click');
    },
    $_updateWidth() {
      const inputElement = this.$refs.input;
      if (!inputElement) return;

      this.inputWidth = inputElement.offsetWidth;
      this.placeholderWidth = this.$_getPlaceholderWidth(inputElement);
    },
    $_getPlaceholderWidth() {
      if (!this.$refs.input || !this.placeholder) return 0;
      const inputElement = this.$refs.input;
      const originalValue = inputElement.value;
      const originalType = inputElement.type;
      inputElement.value = inputElement.placeholder;
      if (originalType === 'password') inputElement.type = 'text';
      const placeholderWidth = inputElement.scrollWidth;
      inputElement.value = originalValue;
      inputElement.type = originalType;
      return placeholderWidth;
    },
  },
};
</script>
<style lang="scss">
@import '@/assets/scss/component/state-input-button';
</style>
